import React, { useCallback, useEffect, useState } from 'react';
import DefaultContainer from '../../components/DefaultContainer';
import { Components } from '~/scenes/HiredInsurance/components';
import { Button, ProtectionResumeItemCard, Select, Text } from '~/components';
import { View } from 'react-native';
import { bagProtectionResumeItems } from '~/utils/services';
import useAppNavigation from '~/hooks/useAppNavigation';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '~/navigation/types';
import { cancelAssurantServiceUseCase } from '~/useCases/AssegurouUseCases/CancelAssurantServiceUseCase';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { getCancelReasonsUseCase } from '~/useCases/AssegurouUseCases/GetCancelReasonsUseCase';
import useToast from '~/hooks/useToast';
import { HiredInsuranceProductType } from '~/domain/Assegurou/contractedServices';

export const schema = yup
  .object()
  .shape({
    cancelReasonId: yup.string().required('Este campo é obrigatório'),
  })
  .required();

const HiredInsuranceCancelBagProtection: React.FC = () => {
  const { navigate, goBack } = useAppNavigation();
  const { showError } = useToast();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ cancelReasonId: string }>({
    resolver: yupResolver(schema),
  });

  const {
    params: { id: certificateCode },
  } =
    useRoute<
      RouteProp<
        RootStackParamList,
        'MarisaServicos_HiredInsuranceCancelBagProtection'
      >
    >();

  const [loading, setLoading] = useState(true);
  const [cancelReasons, setCancelReasons] = useState<
    { label: string; value: string }[] | []
  >([]);

  const cancelContract = useCallback(
    async (data: { cancelReasonId: string }) => {
      const responseSuccess = await cancelAssurantServiceUseCase({
        certificateCode,
        cancelReasonCode: data.cancelReasonId,
      });
      if (responseSuccess) {
        navigate('MarisaServicos_HiredInsuranceCancelSuccessBagProtection');
      } else {
        navigate('MarisaServicos_HiredInsuranceCancelFailBagProtection', {
          id: certificateCode,
        });
      }
    },
    [certificateCode, navigate]
  );

  const fetchCancelReasons = useCallback(async () => {
    setLoading(true);
    try {
      const cancelReasonsResponse = await getCancelReasonsUseCase(
        HiredInsuranceProductType.BAG_PROTECTION
      );
      const formattedCancelReasons = cancelReasonsResponse.map((reason) => ({
        label: reason.description,
        value: reason.code,
      }));
      setCancelReasons(formattedCancelReasons);
    } catch (error) {
      showError({
        title: 'Erro',
        description:
          'Houve um erro ao tentar buscar os motivos do cancelamento. Por favor, tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchCancelReasons();
  }, [fetchCancelReasons]);

  return (
    <DefaultContainer
      type="bag-protection"
      showBanner={false}
      className="py-[42px] items-center justify-center"
    >
      <Components.CancelStatus type="alert" />

      <View className="items-center justify-center">
        <Text className="text-lg text-gray-700 text-center w-[50%]">
          Você quer mesmo cancelar o Seguro{' '}
          <Text className="text-primary-600">Proteção Bolsa</Text>?
        </Text>
      </View>

      <Text className="mt-5 text-center text-sm text-gray-400 mb-5">
        Você deixará de se resguardar de benefícios que podem proteger seus
        pertences.
      </Text>

      {bagProtectionResumeItems.map((item, index) => (
        <ProtectionResumeItemCard data={item} key={index} />
      ))}

      <Select
        control={control}
        loading={loading}
        className="text-sm mt-6"
        label="Caso queira mesmo cancelar, selecione uma opção abaixo:"
        name="cancelReasonId"
        placeholder="Selecione o motivo do cancelamento"
        error={errors.cancelReasonId?.message}
        items={cancelReasons}
        containerStyle={{ flex: 0.3 }}
      />

      <Button className="w-full mt-8" onPress={handleSubmit(cancelContract)}>
        Cancelar Seguro
      </Button>

      <Button variant="tertiary" className="mt-4 w-full" onPress={goBack}>
        VOLTAR
      </Button>
    </DefaultContainer>
  );
};

export default HiredInsuranceCancelBagProtection;
